import { useRef } from "react";
import { motion, useInView } from "framer-motion";
import vs1 from "../../assets/vs1.webp";
import vs2 from "../../assets/vs2.webp";
import vs3 from "../../assets/vs3.webp";
import vs4 from "../../assets/vs4.webp";
/* ================= ANIMATION VARIANTS ================= */

const container = {
  hidden: {},
  visible: {
    transition: {
      staggerChildren: 0.22,
    },
  },
};

const imageReveal = {
  hidden: { opacity: 0, y: 60, scale: 0.95 },
  visible: {
    opacity: 1,
    y: 0,
    scale: 1,
    transition: {
      duration: 0.9,
      ease: [0.22, 1, 0.36, 1],
    },
  },
};

const textReveal = {
  hidden: { opacity: 0, x: 60 },
  visible: {
    opacity: 1,
    x: 0,
    transition: {
      duration: 0.8,
      ease: [0.22, 1, 0.36, 1],
    },
  },
};

/* ================= COMPONENT ================= */

export default function WhyChooseUs() {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-120px" });

  return (
    <section ref={ref} className="w-full bg-white py-16 lg:py-20">

      <motion.div
        variants={container}
        initial="hidden"
        animate={isInView ? "visible" : "hidden"}
        className="max-w-[1300px] mx-auto px-6 lg:px-10 grid grid-cols-1 lg:grid-cols-2 gap-14 lg:gap-20"
      >

        {/* ================= LEFT IMAGE GRID (NEVER CHANGES) ================= */}
        <motion.div
          variants={container}
          className="relative grid grid-cols-2 grid-rows-2 gap-4 sm:gap-6 aspect-[1/1] max-w-[520px] mx-auto lg:mx-0"
        >
          <motion.img
            src={vs1}
            variants={imageReveal}
            className="rounded-2xl object-cover w-full h-full"
          />
          <motion.img
            src={vs2}
            variants={imageReveal}
            className="rounded-2xl object-cover w-full h-full"
          />
          <motion.img
            src={vs3}
            variants={imageReveal}
            className="rounded-2xl object-cover w-full h-full"
          />
          <motion.img
            src={vs4}
            variants={imageReveal}
            className="rounded-2xl object-cover w-full h-full"
          />

          {/* FLOAT CARD (RESPONSIVE SIZE) */}
<div className="absolute inset-0 flex items-center justify-center pointer-events-none">
  <motion.div
    initial={{ opacity: 0, scale: 0.85 }}
    animate={isInView ? { opacity: 1, scale: 1 } : {}}
    transition={{
      duration: 0.6,
      ease: [0.22, 1, 0.36, 1],
    }}
    className="
      bg-[#064BA5] text-white text-center shadow-lg
      px-5 py-4 rounded-lg
      sm:px-7 sm:py-5 sm:rounded-xl
    "
  >
    <h2 className="text-2xl sm:text-3xl font-bold">40+</h2>
    <p className="text-[11px] sm:text-sm mt-1">
      Countries we Serve
    </p>
    <div className="mt-2 text-[10px] sm:text-sm">★★★★★Trusted by Students & Parents</div>
  </motion.div>
</div>

        </motion.div>
        {/* ================= RIGHT CONTENT ================= */}
        <motion.div variants={container} className="text-center lg:text-left">

         <motion.p
  variants={textReveal}
  className="inline-block px-10 py-4 text-lg sm:text-xl font-bold tracking-wide 
             bg-[#2B3E9A] text-white mb-6 shadow-lg cursor-default"
>
  WHY CHOOSE US
</motion.p>



          <motion.h2
            variants={textReveal}
            className="text-2xl sm:text-4xl lg:text-5xl font-serif leading-tight text-[#000000]"
          >
            <span className="text-[#064BA5]">Trusted </span> Global 
            <br />
            Visa Guidance 
          </motion.h2>

          <motion.div
            variants={textReveal}
            className="w-full h-[1px] bg-[#064BA5] my-6"
          />

          <motion.p
            variants={textReveal}
            className="text-gray-500 max-w-xl mx-auto lg:mx-0 text-sm leading-relaxed mb-10"
          >
            We simplify the study abroad visa process with expert guidance, transparent procedures, and end-to-end student support across multiple countries.
          </motion.p>

          <div className="space-y-8">
            {[
  {
    title: "Virtual Interviews Made Easy",
    description:
      "Smooth preparation and guidance for online university and visa interviews.",
  },
  {
    title: "Fast & Transparent Process",
    description:
      "Clear documentation, no hidden charges, and timely updates at every stage.",
  },
  {
    title: "High Visa Approval Success Rate",
    description:
      "Proven expertise in student visa applications across top study destinations.",
  },
].map((item, i) => (

              <motion.div
                key={i}
                variants={textReveal}
                className="flex gap-5 items-start text-left"
              >
                <div className="w-14 h-14 rounded-lg bg-[#f2dadd] flex items-center justify-center shrink-0">
                  <div className="w-7 h-7 border-2 border-[#064BA5] rounded-sm flex items-center justify-center">
                    <svg
                      className="w-5 h-5 text-[#064BA5]"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="3"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        d="M5 13l4 4L19 7"
                      />
                    </svg>
                  </div>
                </div>

                <div className="flex-1">
                  <h4 className="font-semibold text-[#000000] mb-1">
  {item.title}
</h4>
<p className="text-sm text-gray-500 leading-relaxed">
  {item.description}
</p>

                  <div className="w-full h-[1px] bg-[#e7c6cb] mt-4"></div>
                </div>
              </motion.div>
            ))}
          </div>
        </motion.div>
      </motion.div>
    </section>
  );
}
